/* Aperçu d'un e-mail avant envoi (changement d'étape) : objet et corps tels qu'ils partiront, modifiables pour cet envoi. */
import { useEffect, useState } from 'react';
import { ChevronDown, Eye, Loader2, Pencil, RotateCcw } from 'lucide-react';
import type { EmailContent } from './api';
import { errorMessage, inputClass } from './ui';

interface Props {
  /** Charge le contenu rendu (modèle + variables du contact). */
  load: () => Promise<EmailContent>;
  /** Change quand le contenu doit être recalculé (ex. mandat SEPA inclus ou non). */
  loadKey?: string;
  /** Texte modifié pour cet envoi, ou null pour le modèle. */
  edited: EmailContent | null;
  onEdit: (content: EmailContent | null) => void;
  /** Pièce jointe annoncée sous l'objet. */
  attachment?: string;
  label?: string;
}

export function EmailPreview({ load, loadKey = '', edited, onEdit, attachment, label = 'Voir le contenu de l’e-mail' }: Props) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<EmailContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setContent(null);
    load()
      .then(v => { if (!cancelled) setContent(v); })
      .catch(err => { if (!cancelled) setError(errorMessage(err)); });
    return () => { cancelled = true; };
  }, [open, loadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = edited ?? content;
  const pending = shown ? Array.from(new Set((shown.subject + shown.body).match(/\{\{\w+\}\}/g) ?? [])) : [];

  return (
    <div className="text-xs">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="flex items-center gap-1.5 font-medium text-neutral-700 hover:text-neutral-900">
        <Eye className="w-3.5 h-3.5" />{label}
        {edited && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold">modifié</span>}
        <ChevronDown className={`w-3.5 h-3.5 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-neutral-200 bg-white overflow-hidden">
          {error ? (
            <p className="p-3 text-red-700">{error}</p>
          ) : !shown ? (
            <div className="flex items-center gap-2 p-3 text-neutral-400"><Loader2 className="w-3.5 h-3.5 animate-spin" />Préparation de l’aperçu…</div>
          ) : editing ? (
            <div className="p-3 space-y-2">
              <label className="block">
                <span className="block text-[11px] font-medium text-neutral-600 mb-1">Objet</span>
                <input value={shown.subject} onChange={e => onEdit({ ...shown, subject: e.target.value })} className={`${inputClass} border-neutral-200`} />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-neutral-600 mb-1">Message</span>
                <textarea rows={10} value={shown.body} onChange={e => onEdit({ ...shown, body: e.target.value })}
                  className={`${inputClass} border-neutral-200 text-[13px] leading-relaxed font-sans`} />
              </label>
              <div className="flex justify-end">
                <button type="button" onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-[11px] font-semibold hover:bg-neutral-700">
                  Terminer
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-neutral-100 bg-neutral-50 space-y-0.5">
                <p><span className="text-neutral-500">Objet : </span><span className="font-medium text-neutral-900">{shown.subject || '—'}</span></p>
                {attachment && <p className="text-neutral-500">Pièce jointe : {attachment}</p>}
              </div>
              {/* Corps : modèle des Paramètres (HTML autorisé), valeurs du contact échappées à l'insertion. */}
              <div className="px-3 py-2.5 max-h-72 overflow-y-auto text-[13px] leading-relaxed text-neutral-800 [&_a]:text-blue-700 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: shown.body.replace(/\n/g, '<br>') }} />
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-neutral-100">
                <span className="text-[11px] text-neutral-500">
                  {pending.length > 0 ? `Rempli à l’envoi : ${pending.join(', ')}` : 'Texte envoyé tel quel.'}
                </span>
                <span className="flex items-center gap-3">
                  {edited && (
                    <button type="button" onClick={() => onEdit(null)} className="flex items-center gap-1 text-[11px] text-neutral-600 hover:text-neutral-900">
                      <RotateCcw className="w-3 h-3" />Revenir au modèle
                    </button>
                  )}
                  <button type="button" onClick={() => { if (!edited && content) onEdit(content); setEditing(true); }}
                    className="flex items-center gap-1 text-[11px] font-medium text-neutral-700 hover:text-neutral-900">
                    <Pencil className="w-3 h-3" />Modifier pour cet envoi
                  </button>
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
